import React, { useState } from "react";
import { makeStyles } from "@material-ui/core";
import { suspendProject } from "./reviewApi";
import { buildHistory } from "./caseText";
import {
  CONSEQUENCE,
  CopyText,
  InlineConfirm,
  Section,
  TextAction,
  errMsg,
  fmtDay,
  fromNow,
  humanizeFeature,
  pal,
  plural,
  qualityColor,
  sentenceCase,
  shortId,
  titleCase,
  usePal,
} from "./reviewUi";
import { useSnackbarContext } from "../../commons/Snackbar/Snackbar";

// TRUST, SAME DEVICE, OTHER PROJECTS OF THIS OWNER, ACCOUNT and HISTORY.

const useStyles = makeStyles((theme) => {
  const c = pal(theme);
  return {
    ruled: { maxWidth: 520 },
    ruledRow: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) auto",
      gap: 16,
      padding: "7px 0",
      borderBottom: `1px solid ${c.line2}`,
      color: c.text2,
    },
    pts: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
    rows: { maxWidth: 620 },
    row: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) auto",
      gap: 16,
      alignItems: "center",
      padding: "9px 0",
      borderBottom: `1px solid ${c.line2}`,
    },
    clickable: { cursor: "pointer", "&:hover $rowName": { color: c.primary } },
    rowName: { color: c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    rowSub: { color: c.hint, fontSize: 12 },
    rowRight: { display: "flex", alignItems: "center", gap: 14, fontSize: 12.5 },
    quiet: { color: c.text2, "&:hover": { color: c.error, textDecoration: "none" } },
    idCase: { textTransform: "none", letterSpacing: 0 },
    sub: { color: c.hint, fontSize: 12, marginTop: -6, marginBottom: 6 },
    none: { color: c.hint, fontSize: 13 },
    facts: {
      display: "grid",
      gridTemplateColumns: "160px minmax(0, 1fr)",
      rowGap: 8,
      columnGap: 16,
      maxWidth: 560,
      "& > div:nth-child(odd)": { color: c.hint },
      "& > div:nth-child(even)": { wordBreak: "break-word" },
    },
    hist: {
      display: "grid",
      gridTemplateColumns: "130px minmax(0, 1fr)",
      columnGap: 16,
      padding: "7px 0",
      borderBottom: `1px solid ${c.line2}`,
      maxWidth: 720,
    },
    histDate: { color: c.hint, fontSize: 12, paddingTop: 1 },
  };
});

const signed = (n) => `${n >= 0 ? "+" : "−"}${Math.abs(n)}`;

export function TrustSection({ breakdown, trust }) {
  const s = useStyles();
  const c = usePal();
  const t = breakdown || trust;
  const contributions = [...(breakdown?.contributions || [])].sort((a, b) => {
    const pa = a.points || 0;
    const pb = b.points || 0;
    if ((pa < 0) !== (pb < 0)) return pa < 0 ? -1 : 1;
    return Math.abs(pb) - Math.abs(pa);
  });
  const forced = breakdown?.forcedReview || [];
  return (
    <Section title="Trust">
      {!t ? (
        <div className={s.none}>No trust score computed yet.</div>
      ) : !contributions.length && !forced.length ? (
        <div className={s.none}>No breakdown recorded; only the summary score is stored.</div>
      ) : (
        <div className={s.ruled}>
          {forced.map((f) => (
            <div key={f} className={s.ruledRow} style={{ color: c.error }}>
              <div>{humanizeFeature(f)}</div>
              <div className={s.pts}>forces review</div>
            </div>
          ))}
          {contributions.map((x) => (
            <div key={x.feature} className={s.ruledRow}>
              <div>{humanizeFeature(x.feature)}</div>
              <div className={s.pts} style={{ color: (x.points || 0) < 0 ? c.error : c.resolved }}>
                {signed(x.points || 0)}
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

const STRIKE_TEXT = { block: "Template blocked", deleted: "Template deleted", suspended: "Suspended" };

function LinkedRow({ account, state, canWrite, onOpenCase, onChanged }) {
  const s = useStyles();
  const c = usePal();
  const { showSnackbar } = useSnackbarContext() || {};
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const run = async (note) => {
    setBusy(true);
    setError("");
    try {
      await suspendProject(account.assistantId, note);
      showSnackbar && showSnackbar("Project suspended", "success");
      setConfirm(false);
      onChanged();
    } catch (e) {
      setError(errMsg(e, "Couldn't suspend the project"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className={s.row}>
        <div style={{ minWidth: 0 }}>
          <div className={s.rowName}>{account.assistantName || shortId(account.assistantId)}</div>
          <div className={s.rowSub}>{state}</div>
        </div>
        <div className={s.rowRight}>
          {account.suspended ? (
            <span style={{ color: c.error }}>Suspended</span>
          ) : (
            <>
              {canWrite ? (
                <TextAction inline className={s.quiet} disabled={confirm} onClick={() => setConfirm(true)}>
                  Suspend project
                </TextAction>
              ) : null}
              <TextAction inline tone="primary" onClick={() => onOpenCase(account.assistantId)}>
                Open
              </TextAction>
            </>
          )}
        </div>
      </div>
      {confirm ? (
        <InlineConfirm
          danger
          style={{ marginBottom: 8 }}
          consequence={CONSEQUENCE.suspend_project}
          confirmLabel="Suspend project"
          busy={busy}
          error={error}
          onConfirm={run}
          onCancel={() => {
            setConfirm(false);
            setError("");
          }}
        />
      ) : null}
    </>
  );
}

export function DeviceSection({ devices, canWrite, onOpenCase, onChanged }) {
  const s = useStyles();
  const list = devices || [];
  if (!list.length) {
    return (
      <Section title="Same device">
        <div className={s.none}>No device fingerprint on record.</div>
      </Section>
    );
  }
  return list.map((d) => {
    const lastStrike = (id) => (d.strikes || []).find((x) => x.assistantId === id);
    return (
      <Section
        key={d.deviceId}
        title={
          <>
            Same device · <CopyText value={d.deviceId} label="Copy device id"><span className={s.idCase}>{shortId(d.deviceId)}</span></CopyText>
          </>
        }
      >
        <div className={s.sub}>
          {plural(d.accountCount || 0, "account")} · {plural(d.strikeCount || 0, "strike")} · first seen {fromNow(d.firstSeen)}
        </div>
        {(d.linkedAccounts || []).length ? (
          <div className={s.rows}>
            {d.linkedAccounts.map((a) => {
              const st = lastStrike(a.assistantId);
              const state = st
                ? `${STRIKE_TEXT[st.reason] || sentenceCase(st.reason)} ${fromNow(st.at)}`
                : a.suspended
                  ? "Suspended"
                  : "No strikes";
              return (
                <LinkedRow
                  key={a.assistantId}
                  account={a}
                  state={state}
                  canWrite={canWrite}
                  onOpenCase={onOpenCase}
                  onChanged={onChanged}
                />
              );
            })}
          </div>
        ) : (
          <div className={s.none}>Only this project has used this device.</div>
        )}
      </Section>
    );
  });
}

export function OwnerProjects({ projects, onOpenCase }) {
  const s = useStyles();
  const c = usePal();
  const list = projects || [];
  return (
    <Section title="Other projects of this owner">
      {!list.length ? (
        <div className={s.none}>This owner has no other project.</div>
      ) : (
        <div className={s.rows}>
          {list.map((p) => (
            <div
              key={p.assistantId}
              className={`${s.row} ${s.clickable}`}
              role="button"
              tabIndex={0}
              onClick={() => onOpenCase(p.assistantId)}
              onKeyDown={(e) => e.key === "Enter" && onOpenCase(p.assistantId)}
            >
              <div style={{ minWidth: 0 }}>
                <div className={s.rowName}>{p.assistantName || shortId(p.assistantId)}</div>
                <div className={s.rowSub}>
                  {[p.status ? sentenceCase(p.status) : null, plural(p.violationCount || 0, "violation")]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
              <div className={s.rowRight}>
                {p.suspended ? (
                  <span style={{ color: c.error }}>Suspended{p.suspensionScope ? ` (${p.suspensionScope})` : ""}</span>
                ) : (
                  <span style={{ color: c.primary }}>Open</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

const verified = (v) => (v == null ? "—" : v ? "Verified" : "Not verified");
const withAgo = (v) => (v ? `${fmtDay(v)} · ${fromNow(v)}` : "—");

export function AccountFacts({ project, owner, clientId }) {
  const s = useStyles();
  const c = usePal();
  const p = project || {};
  const facts = [
    ["Plan", p.activePlan ? titleCase(String(p.activePlan).toLowerCase()) : "—"],
    [
      "Quality rating",
      p.qualityRating ? <span style={{ color: qualityColor(c, p.qualityRating) }}>{sentenceCase(p.qualityRating)}</span> : "—",
    ],
    ["Status", p.status ? sentenceCase(p.status) : "—"],
    ["Country", p.country || "—"],
    // Plain text on purpose: customer-supplied, possibly malicious.
    ["Website", p.website || "—"],
    ["Project created", withAgo(p.createdAt)],
    ["WABA activated", withAgo(p.wabaActivatedOn)],
    ["Facebook business", verified(p.fbManagerVerified)],
    ["Display name", verified(p.displayNameVerified)],
    ["Owner email", owner ? verified(owner.emailVerified) : "—"],
    ["Partner", p.partnerId ? <CopyText value={p.partnerId} label="Copy partner id">{shortId(p.partnerId)}</CopyText> : "Direct"],
    ["Owner client id", clientId ? <CopyText value={clientId} label="Copy owner client id">{clientId}</CopyText> : "—"],
  ];
  return (
    <Section title="Account">
      <div className={s.facts}>
        {facts.map(([k, v]) => (
          <React.Fragment key={k}>
            <div>{k}</div>
            <div>{v}</div>
          </React.Fragment>
        ))}
      </div>
    </Section>
  );
}

export function History({ dossier }) {
  const s = useStyles();
  const c = usePal();
  const events = buildHistory(dossier);
  return (
    <Section title="History">
      {!events.length ? (
        <div className={s.none}>No decisions yet.</div>
      ) : (
        events.map((e, i) => (
          <div key={i} className={s.hist}>
            <div className={s.histDate}>{fmtDay(e.at)}</div>
            <div>
              <span style={{ color: e.danger ? c.error : c.text }}>{e.action}</span>
              <span style={{ color: c.hint }}> · {e.by || "—"}</span>
              {e.note ? <span style={{ color: c.text2 }}> · “{e.note}”</span> : null}
            </div>
          </div>
        ))
      )}
    </Section>
  );
}
